import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import {
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

export function ExpiringCertificatesWidget({ data, loading }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  
  if (loading) {
    return (
      <div className="dashboard-card bg-card border border-border rounded-lg p-5">
        <h3 className="font-heading text-lg text-foreground mb-4 flex items-center gap-2">
          <AlertTriangle className="text-orange-400" size={20} />
          {t('dashboard.expiringCertificates')}
        </h3>
        <div className="text-center py-8 text-muted-foreground animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  const expiring = data?.expiring || [];
  const expired = data?.expired || [];
  const all = [...expired, ...expiring];
  const displayItems = expanded ? all : all.slice(0, 5);

  return (
    <div className="dashboard-card glass-widget rounded-lg p-5 opacity-0 animate-scale-in stagger-4" data-testid="expiring-certificates-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-orange-500/20 rounded-lg">
            <AlertTriangle className="text-orange-400" size={18} />
          </div>
          <div>
            <h3 className="font-heading text-lg text-foreground">{t('dashboard.certificates')}</h3>
            <p className="text-xs text-muted-foreground">{t('dashboard.expiring')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {expired.length > 0 && (
            <span className="text-xs bg-red-500/20 text-red-400 px-2.5 py-1 rounded-full font-medium" data-testid="expired-count">
              {expired.length} {t('dashboard.expiredCount')}
            </span>
          )}
          {expiring.length > 0 && (
            <span className="text-xs bg-orange-500/20 text-orange-400 px-2.5 py-1 rounded-full font-medium" data-testid="expiring-count">
              {expiring.length} {t('dashboard.expiring')}
            </span>
          )}
        </div>
      </div>
      
      {all.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <CheckCircle size={32} className="mx-auto mb-2 text-emerald-400/50" />
          <p className="text-sm">{t('dashboard.allValid')}</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {displayItems.map((cert, idx) => (
              <div 
                key={cert.id || idx} 
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <div className="min-w-0 flex-1 mr-3">
                  <p className="text-sm font-medium text-foreground truncate">{cert.product}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {cert.fraction && (
                      <span className="text-xs text-muted-foreground font-mono">{cert.fraction}</span>
                    )}
                    {cert.importer_name && (
                      <span className="text-xs text-muted-foreground truncate">{cert.importer_name}</span>
                    )}
                  </div>
                </div>
                <span className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  cert.status === 'expired' 
                    ? 'bg-red-500/20 text-red-400' 
                    : cert.days_until_expiration <= 7
                      ? 'bg-orange-500/20 text-orange-400'
                      : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {cert.status === 'expired' 
                    ? `${t('dashboard.expiredDaysAgo')} ${Math.abs(cert.days_until_expiration)}d`
                    : `${cert.days_until_expiration}d ${t('dashboard.daysRemaining')}`
                  }
                </span>
              </div>
            ))}
          </div>
          {all.length > 5 && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-3 py-2 text-sm text-primary hover:text-primary/80 flex items-center justify-center gap-1 transition-colors"
            >
              {expanded ? (
                <>{t('dashboard.viewLess')} <ChevronUp size={14} /></>
              ) : (
                <>{t('dashboard.viewMore')} {all.length - 5} {t('dashboard.more')} <ChevronDown size={14} /></>
              )}
            </button>
          )}
          <Link to="/certificates" className="block mt-3">
            <button className="w-full py-2 text-sm text-primary hover:text-primary/80 flex items-center justify-center gap-1 transition-colors border-t border-border/50 pt-3">
              {t('dashboard.viewAllCertificates')} <ArrowRight size={14} />
            </button>
          </Link>
        </>
      )}
    </div>
  );
}
